import Keycloak from 'keycloak-js'
import type { KeycloakInitOptions } from 'keycloak-js'

// Keycloak configuration
const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || '',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'sgrc',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'frontend-client',
}

console.log('[Keycloak] Config:', keycloakConfig)
console.log('[Keycloak] Current URL:', window.location.href)
console.log('[Keycloak] Query params:', window.location.search)
console.log('[Keycloak] Hash:', window.location.hash)

// Create Keycloak instance
export const keycloak = new Keycloak(keycloakConfig)

// --- Single-flight refresh-token mutex -------------------------------------
// Keycloak's realm enforces a single-use refresh_token policy (rotation).
// Under React Strict-Mode double-mount, parallel axios calls, or rapid
// page.goto() storms, multiple callers can hit keycloak.updateToken() at the
// same moment. The first call POSTs the saved refresh_token; the second
// concurrent call POSTs the SAME refresh_token, which the IdP has already
// invalidated -> invalid_grant 400 -> the whole session is dropped.
//
// Wrap updateToken with a shared Promise so concurrent callers de-dupe to a
// single in-flight refresh and all await the same resolution.
const __originalUpdateToken = keycloak.updateToken.bind(keycloak)
let __pendingRefresh: Promise<boolean> | null = null
keycloak.updateToken = ((minValidity?: number) => {
  if (__pendingRefresh) return __pendingRefresh
  __pendingRefresh = __originalUpdateToken(minValidity).finally(() => {
    __pendingRefresh = null
  })
  return __pendingRefresh
}) as typeof keycloak.updateToken

// Track initialization state to prevent double initialization in React Strict Mode
let isInitialized = false
let initPromise: Promise<boolean> | null = null

// Safe initialization function that prevents double initialization
export async function initKeycloakSafe(options: KeycloakInitOptions): Promise<boolean> {
  if (isInitialized) {
    console.log('[Keycloak] Already initialized, authenticated:', keycloak.authenticated)
    return keycloak.authenticated || false
  }

  if (initPromise) {
    console.log('[Keycloak] Init already in progress, waiting...')
    return initPromise
  }

  const hasCodeInQuery = window.location.search.includes('code=')
  const hasCodeInHash = window.location.hash.includes('code=')

  console.log('[Keycloak] Starting init...', {
    options: JSON.stringify(options),
    hasCodeInQuery,
    hasCodeInHash,
    href: window.location.href,
  })

  initPromise = keycloak.init(options).then((authenticated) => {
    isInitialized = true
    console.log('[Keycloak] Init SUCCESS:', {
      authenticated,
      hasToken: !!keycloak.token,
      hasRefreshToken: !!keycloak.refreshToken,
      tokenParsed: keycloak.tokenParsed ? { sub: keycloak.tokenParsed.sub, email: (keycloak.tokenParsed as any).email } : null,
    })
    return authenticated
  }).catch((error) => {
    // Don't block the app — just return false (unauthenticated)
    isInitialized = true
    initPromise = null
    console.error('[Keycloak] Init FAILED:', {
      error,
      errorMessage: error?.message,
      errorStack: error?.stack,
      type: typeof error,
    })
    return false
  })

  return initPromise
}

// Initialize options — keep minimal for reliability
export const keycloakIdpHint = import.meta.env.VITE_KC_IDP_HINT || undefined

// Detect if URL has a KC callback code — use 'check-sso' only then to exchange.
// Otherwise no onLoad (avoid 3p-cookie iframe block).
const hasKcCode = typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).has('code') ||
   window.location.hash.includes('code='))

export const keycloakInitOptions: KeycloakInitOptions = {
  pkceMethod: 'S256',
  checkLoginIframe: false,
  enableLogging: true,
  // If we have a callback code, use check-sso to exchange; otherwise no onLoad.
  // This bypasses keycloak-js's 3rd-party-cookie iframe check (Chrome blocks
  // it by default — "Timeout when waiting for 3rd party check iframe message").
  ...(hasKcCode ? { onLoad: 'check-sso' as const } : {}),
}

// Get tenant ID from token
export function getTenantId(): string | null {
  if (keycloak.tokenParsed) {
    return (keycloak.tokenParsed as any).tenant_id || null
  }
  return null
}

// Get the current user's id (Keycloak subject) from token
export function getUserId(): string | null {
  if (keycloak.tokenParsed) {
    return (keycloak.tokenParsed as any).sub || null
  }
  return null
}

// Get user roles from token
export function getUserRoles(): string[] {
  if (keycloak.tokenParsed?.realm_access?.roles) {
    return keycloak.tokenParsed.realm_access.roles
  }
  return []
}

// Check if user has a specific role
export function hasRole(role: string): boolean {
  return getUserRoles().includes(role)
}

// Check if user has any of the specified roles
export function hasAnyRole(roles: string[]): boolean {
  const userRoles = getUserRoles()
  return roles.some((role) => userRoles.includes(role))
}

// Get portal access from token
export function getPortalAccess(): string[] {
  if (keycloak.tokenParsed) {
    const raw = (keycloak.tokenParsed as any).portal_access
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string') return raw.split(',').map((s: string) => s.trim())
  }
  return ['client']
}

// Get account type from token
export function getAccountType(): string | null {
  if (keycloak.tokenParsed) return (keycloak.tokenParsed as any).account_type || null
  return null
}

// Get tenant type from token
export function getTenantType(): string | null {
  if (keycloak.tokenParsed) return (keycloak.tokenParsed as any).tenant_type || null
  return null
}

// Refresh token before it expires
export async function refreshToken(minValidity = 30): Promise<boolean> {
  try {
    const refreshed = await keycloak.updateToken(minValidity)
    return refreshed
  } catch (error) {
    console.error('Failed to refresh token', error)
    return false
  }
}
